/**
 * BillingContent - Billing and subscription settings panel
 *
 * Displays real subscription status, credit balance, usage breakdown,
 * and management actions (Stripe Checkout, Customer Portal, spend limits).
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui';
import { billingApi } from '../../services/api';
import { logger } from '../../utils/logger';

interface SubscriptionData {
  plan: string;
  status: string;
  seatCount: number;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  monthlySpendLimitCents?: number;
  perUserSpendLimitCents?: number;
}

interface CreditBalanceData {
  includedCreditsCents: number;
  consumedCreditsCents: number;
  remainingCreditsCents: number;
  overageUsageCents: number;
  projectedMonthEndCents: number;
}

interface UsageSummary {
  totalCustomerCostCents: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  requestCount: number;
  byModel: Record<string, { costCents: number; requests: number }>;
  byUser: Record<string, { costCents: number; requests: number }>;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function BillingContent() {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [creditBalance, setCreditBalance] = useState<CreditBalanceData | null>(null);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadBillingData = useCallback(async () => {
    try {
      setError(null);
      const [subData, usageData] = await Promise.all([
        billingApi.getCurrentSubscription().catch(() => null),
        billingApi.getUsageSummary(getCurrentPeriod()).catch(() => null),
      ]);

      if (subData) {
        setSubscription(subData.subscription || subData);
        setCreditBalance(subData.creditBalance || null);
      }
      if (usageData) {
        setUsageSummary(usageData);
      }
    } catch (err) {
      logger.error('Failed to load billing data:', err);
      setError('Failed to load billing information.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const handleManageSubscription = async () => {
    try {
      const { portalUrl } = await billingApi.createPortalSession();
      window.open(portalUrl, '_blank');
    } catch (err) {
      logger.error('Failed to open billing portal:', err);
    }
  };

  const handleSubscribe = async () => {
    try {
      const { checkoutUrl } = await billingApi.createCheckoutSession(3);
      window.location.href = checkoutUrl;
    } catch (err) {
      logger.error('Failed to create checkout session:', err);
    }
  };

  const handleExportCsv = async () => {
    try {
      const period = getCurrentPeriod();
      const startDate = `${period}-01`;
      const endDate = getLastDayOfMonth(period);
      const blob = await billingApi.exportUsageCsv(startDate, endDate);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usage-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Failed to export CSV:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading billing information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-destructive">{error}</div>
      </div>
    );
  }

  const planLabel = subscription?.plan
    ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)
    : 'No Plan';

  const statusLabel = subscription?.status
    ? subscription.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Inactive';

  const statusColor = subscription?.status === 'active'
    ? 'bg-success-light text-success'
    : subscription?.status === 'trialing'
      ? 'bg-info-light text-info'
      : subscription?.status === 'past_due'
        ? 'bg-warning-light text-warning'
        : 'bg-muted text-muted-foreground';

  const creditPercentage = creditBalance && creditBalance.includedCreditsCents > 0
    ? Math.min((creditBalance.consumedCreditsCents / creditBalance.includedCreditsCents) * 100, 100)
    : 0;

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
        <h2 className="text-sm font-medium text-foreground flex-1">Billing</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-card space-y-4">
        {/* Subscription Status */}
        <div className="bg-gradient-to-r from-primary to-primary/80 rounded-lg p-6 text-primary-foreground">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm opacity-80">Current Plan</p>
              <h3 className="text-2xl font-bold">{planLabel}</h3>
              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${statusColor}`}>
                {statusLabel}
              </span>
            </div>
            <div className="text-right">
              {subscription?.plan === 'professional' && (
                <>
                  <p className="text-3xl font-bold">$299</p>
                  <p className="text-sm opacity-80">
                    /seat/month &middot; {subscription.seatCount} seat{subscription.seatCount !== 1 ? 's' : ''}
                  </p>
                </>
              )}
              {subscription?.plan === 'trial' && (
                <p className="text-lg font-medium">14-Day Trial</p>
              )}
              {!subscription && (
                <p className="text-lg font-medium">No Active Plan</p>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            {subscription?.status === 'active' && (
              <Button variant="secondary" size="sm" onClick={handleManageSubscription}>
                Manage Subscription
              </Button>
            )}
            {(!subscription || subscription.status === 'trial_expired' || subscription.status === 'canceled') && (
              <Button variant="secondary" size="sm" onClick={handleSubscribe}>
                Subscribe — $299/seat/month
              </Button>
            )}
            <Button variant="info" size="sm" onClick={handleExportCsv}>
              Export Usage CSV
            </Button>
          </div>
        </div>

        {/* Credit Balance */}
        {creditBalance && (
          <div className="bg-muted/30 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-foreground mb-4">AI Credit Balance</h3>

            <div className="flex items-end justify-between mb-2">
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatCents(creditBalance.remainingCreditsCents)}
                </p>
                <p className="text-xs text-muted-foreground">remaining</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">
                  {formatCents(creditBalance.consumedCreditsCents)} / {formatCents(creditBalance.includedCreditsCents)}
                </p>
              </div>
            </div>

            <div className="h-2 bg-muted rounded-full overflow-hidden mb-3">
              <div
                className={`h-full rounded-full transition-all ${
                  creditPercentage > 90 ? 'bg-destructive' : creditPercentage > 70 ? 'bg-warning' : 'bg-primary'
                }`}
                // eslint-disable-next-line no-restricted-syntax
                style={{ width: `${creditPercentage}%` }}
              />
            </div>

            {creditBalance.overageUsageCents > 0 && (
              <p className="text-xs text-warning">
                Overage: {formatCents(creditBalance.overageUsageCents)} (billed at standard rate)
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Projected end of month: {formatCents(creditBalance.projectedMonthEndCents)}
            </p>
          </div>
        )}

        {/* Usage Summary */}
        {usageSummary && (
          <div className="bg-muted/30 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              Usage This Period
            </h3>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Total Cost</p>
                <p className="text-xl font-bold text-foreground">
                  {formatCents(usageSummary.totalCustomerCostCents)}
                </p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Requests</p>
                <p className="text-xl font-bold text-foreground">
                  {usageSummary.requestCount.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Input Tokens</p>
                <p className="text-xl font-bold text-foreground">
                  {(usageSummary.totalInputTokens / 1000).toFixed(0)}K
                </p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-xs text-muted-foreground">Output Tokens</p>
                <p className="text-xl font-bold text-foreground">
                  {(usageSummary.totalOutputTokens / 1000).toFixed(0)}K
                </p>
              </div>
            </div>

            {/* Usage by Model */}
            {usageSummary.byModel && Object.keys(usageSummary.byModel).length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">By Model</p>
                <div className="space-y-1">
                  {Object.entries(usageSummary.byModel).map(([model, data]) => (
                    <div key={model} className="flex items-center justify-between text-sm">
                      <span className="text-foreground capitalize">{model}</span>
                      <span className="text-muted-foreground">
                        {formatCents(data.costCents)} &middot; {data.requests} requests
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Spend Controls */}
        {subscription && ['active', 'trialing'].includes(subscription.status) && (
          <div className="bg-muted/30 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-foreground mb-4">Spend Controls</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Monthly spend limit</span>
                <span className="text-sm text-muted-foreground">
                  {subscription.monthlySpendLimitCents
                    ? formatCents(subscription.monthlySpendLimitCents)
                    : 'No limit'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Per-user spend limit</span>
                <span className="text-sm text-muted-foreground">
                  {subscription.perUserSpendLimitCents
                    ? formatCents(subscription.perUserSpendLimitCents)
                    : 'No limit'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Renewal Info */}
        {subscription?.currentPeriodEnd && (
          <div className="bg-info-light border border-info rounded-lg p-4">
            <p className="text-sm text-info">
              {subscription.cancelAtPeriodEnd
                ? `Your subscription will end on ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}.`
                : `Next renewal: ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}
            </p>
          </div>
        )}

        {/* Support */}
        <div className="bg-info-light border border-info rounded-lg p-4">
          <p className="text-sm text-info">
            <strong>Need help?</strong> Contact our support team at billing@aerotage.com
            for any billing questions or to discuss enterprise plans.
          </p>
        </div>
      </div>
    </div>
  );
}

function getCurrentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getLastDayOfMonth(period: string): string {
  const [year, month] = period.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${period}-${String(lastDay).padStart(2, '0')}`;
}
